import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { FiSend, FiImage, FiX } from "react-icons/fi";
import { sendEmailVerification } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import styles from "./SupportWidget.module.css";
import { auth, storage } from "../../Confg/firebaseConfig";
import { getApiUrl } from "../../Confg/appConfig";
import { useUser } from "../../Confg/UserContext";

const PERSONA = "Nina";

function getBaseUrl() {
    return String(getApiUrl() || "https://ifood.onrender.com").replace(/\/$/, "");
}

function api(metodo, caminho, corpo) {
    const url = `${getBaseUrl()}/api/nina-web/${caminho}`;
    return (metodo === "get" ? axios.get(url) : axios.post(url, corpo)).then((r) => r.data);
}

function motivo(err) {
    return err?.response?.data?.error || err?.response?.data?.message || "Não deu pra falar com a Nina agora.";
}

const NinaChat = ({ onHandoffRequested }) => {
    const { role, merchantData, user } = useUser();
    const isStaff = role === "admin" || role === "support";
    const currentUid = user?.uid || auth.currentUser?.uid;
    const storageKey = currentUid ? `nina_chat_history_${currentUid}` : "nina_chat_history_guest";

    // Inicia imediatamente como "pronto" se já temos a loja no contexto (evita o "Procurando a sua loja...")
    const [estado, setEstado] = useState(() => {
        if (merchantData?.name || currentUid) {
            return {
                etapa: "pronto",
                loja: {
                    nome: merchantData?.name || "Sua Loja",
                    id: merchantData?.id || currentUid,
                },
                fontes: { firebase: true },
            };
        }
        return { etapa: "carregando" };
    });

    const [rotaUtilizada, setRotaUtilizada] = useState("nina-web");
    const [texto, setTexto] = useState("");
    const [fotoFile, setFotoFile] = useState(null);
    const [fotoPreview, setFotoPreview] = useState(null);
    const [uploadingFoto, setUploadingFoto] = useState(false);

    // Carrega o histórico salvo localmente do usuário para não sumir ao fechar o chat
    const [mensagens, setMensagens] = useState(() => {
        try {
            const salvo = localStorage.getItem(storageKey);
            return salvo ? JSON.parse(salvo) : [];
        } catch {
            return [];
        }
    });

    // Se o usuário carregar logo após a montagem, sincroniza o histórico dele salvo no navegador
    useEffect(() => {
        if (!currentUid) return;
        try {
            const salvo = localStorage.getItem(`nina_chat_history_${currentUid}`);
            if (salvo) {
                const parsed = JSON.parse(salvo);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    setMensagens((atuais) => (atuais.length === 0 ? parsed : atuais));
                }
            }
        } catch (e) {
            console.warn("Erro ao restaurar histórico:", e);
        }
    }, [currentUid]);

    const [ocupado, setOcupado] = useState(false);
    const [linkEnviado, setLinkEnviado] = useState(false);
    const [abaValidacao, setAbaValidacao] = useState("whatsapp"); // "whatsapp" | "email"
    const [telefoneWa, setTelefoneWa] = useState("");
    const [codigoWa, setCodigoWa] = useState("");
    const [waEnviado, setWaEnviado] = useState(false);
    const [enviandoWa, setEnviandoWa] = useState(false);
    const [verificandoWa, setVerificandoWa] = useState(false);
    const [erroWa, setErroWa] = useState("");
    const [msgWa, setMsgWa] = useState("");
    const [cooldownWa, setCooldownWa] = useState(0);
    const [cooldownEmail, setCooldownEmail] = useState(0);
    const [transferindo, setTransferindo] = useState(false);
    const [erroTransferencia, setErroTransferencia] = useState("");
    const fimRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (merchantData?.telefone || merchantData?.phone) {
            setTelefoneWa(merchantData.telefone || merchantData.phone);
        }
    }, [merchantData]);

    // Contadores regressivos para os botões de reenvio
    useEffect(() => {
        if (cooldownWa <= 0) return;
        const timer = setInterval(() => setCooldownWa((c) => Math.max(0, c - 1)), 1000);
        return () => clearInterval(timer);
    }, [cooldownWa]);

    useEffect(() => {
        if (cooldownEmail <= 0) return;
        const timer = setInterval(() => setCooldownEmail((c) => Math.max(0, c - 1)), 1000);
        return () => clearInterval(timer);
    }, [cooldownEmail]);

    // Salva o histórico sempre que as mensagens mudam
    useEffect(() => {
        try {
            if (mensagens.length > 0) {
                localStorage.setItem(storageKey, JSON.stringify(mensagens.slice(-50)));
            }
        } catch (e) {
            console.warn("Não foi possível salvar o histórico da Nina:", e);
        }
    }, [mensagens, storageKey]);

    const carregar = useCallback(async () => {
        // Se ainda não tem loja no contexto, marca como carregando. Caso já tenha, faz verificação silenciosa em segundo plano.
        if (!merchantData?.name && !currentUid) {
            setEstado({ etapa: "carregando" });
        }
        try {
            const r = await api("get", "loja");
            setRotaUtilizada("nina-web");
            setEstado({
                etapa: "pronto",
                loja: {
                    nome: r?.loja?.nome || merchantData?.name || "Sua Loja",
                    ...r?.loja,
                },
                fontes: r?.fontes || { firebase: true },
            });
        } catch (err) {
            const errMsg = motivo(err);
            const errLower = errMsg.toLowerCase();

            if (errLower.includes("goby") || errLower.includes("loja") || errLower.includes("permissão") || err?.response?.status === 404 || err?.response?.status === 403) {
                setRotaUtilizada("nina-web");
                setEstado({
                    etapa: "pronto",
                    loja: { nome: merchantData?.name || "Sua Loja" },
                    fontes: { firebase: true },
                });
                return;
            }

            if (isStaff) {
                setRotaUtilizada("admin");
                setEstado({
                    etapa: "pronto",
                    loja: { nome: merchantData?.name || "Auuii Painel" },
                    fontes: { assistenteGeral: true },
                });
                return;
            }

            // Se já temos a loja pelo contexto do painel, mantém pronto e não bloqueia a tela
            if (merchantData?.name || currentUid) {
                setEstado((prev) => ({
                    etapa: "pronto",
                    loja: { nome: merchantData?.name || "Sua Loja", ...prev?.loja },
                    fontes: { firebase: true },
                }));
                return;
            }

            setEstado({ etapa: "erro", code: err?.response?.data?.code || null, erro: errMsg });
        }
    }, [isStaff, merchantData, currentUid]);

    useEffect(() => { carregar(); }, [carregar]);
    useEffect(() => { fimRef.current?.scrollIntoView({ block: "end", behavior: "smooth" }); }, [mensagens, ocupado]);

    async function mandarLink() {
        if (cooldownEmail > 0) return;
        try {
            await sendEmailVerification(auth.currentUser);
            setLinkEnviado(true);
            setCooldownEmail(30);
            setMsgWa("Link de confirmação enviado para seu e-mail!");
        } catch (err) {
            setEstado((e) => ({ ...e, erro: "Não deu pra mandar o link agora. Tente de novo em alguns minutos." }));
        }
    }

    async function enviarCodigoWa() {
        if (cooldownWa > 0) return;
        if (!telefoneWa.trim()) {
            setErroWa("Informe seu número de WhatsApp com DDD.");
            return;
        }
        setEnviandoWa(true);
        setErroWa("");
        setMsgWa("");
        try {
            await api("post", "whatsapp/enviar-codigo", { telefone: telefoneWa.trim() });
            setWaEnviado(true);
            setCooldownWa(30);
            setMsgWa("Código de confirmação enviado para o WhatsApp!");
        } catch (err) {
            setErroWa(motivo(err));
        } finally {
            setEnviandoWa(false);
        }
    }

    async function validarCodigoWa() {
        const cod = codigoWa.trim();
        if (!cod || cod.length !== 6) {
            setErroWa("Digite o código de 6 dígitos recebido.");
            return;
        }
        setVerificandoWa(true);
        setErroWa("");
        try {
            await api("post", "whatsapp/verificar-codigo", { codigo: cod });
            await auth.currentUser?.reload();
            await auth.currentUser?.getIdToken(true);
            setMsgWa("WhatsApp confirmado com sucesso!");
            setTimeout(() => {
                carregar();
            }, 600);
        } catch (err) {
            setErroWa(motivo(err));
        } finally {
            setVerificandoWa(false);
        }
    }

    async function jaConfirmei() {
        try {
            await auth.currentUser?.reload();
            await auth.currentUser?.getIdToken(true);
        } catch { /* segue */ }
        carregar();
    }

    async function transferirParaAtendimento(conversa = mensagens) {
        if (!onHandoffRequested || transferindo) return;
        setTransferindo(true);
        setErroTransferencia("");
        try {
            try {
                await api("post", "handoff", { motivo: "Lojista solicitou atendimento humano no chat" });
            } catch (_) {}
            await onHandoffRequested(conversa);
        } catch (err) {
            console.error("Erro ao transferir conversa para o suporte:", err);
            setErroTransferencia("Não foi possível transferir agora. Tente novamente.");
        } finally {
            setTransferindo(false);
        }
    }

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            alert("Por favor, selecione apenas arquivos de imagem (PNG, JPG, WEBP).");
            return;
        }
        setFotoFile(file);
        setFotoPreview(URL.createObjectURL(file));
    };

    const handleRemoveFoto = () => {
        setFotoFile(null);
        if (fotoPreview) URL.revokeObjectURL(fotoPreview);
        setFotoPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    async function mandar(e) {
        e.preventDefault();
        const msg = texto.trim();
        if ((!msg && !fotoFile) || ocupado || uploadingFoto) return;

        let fotoUrl = null;
        setOcupado(true);

        // Upload da foto no Firebase Storage se houver arquivo
        if (fotoFile) {
            setUploadingFoto(true);
            try {
                const uid = auth.currentUser?.uid || "guest";
                const path = `suporte_fotos/${uid}/${Date.now()}_${fotoFile.name.replace(/[^a-zA-Z0-9.-]/g, "_")}`;
                const storageRef = ref(storage, path);
                const snapshot = await uploadBytes(storageRef, fotoFile);
                fotoUrl = await getDownloadURL(snapshot.ref);
            } catch (upErr) {
                console.error("Erro no upload da foto de suporte:", upErr);
                setMensagens((m) => [...m, { autor: "erro", texto: "Falha ao enviar a foto. Tente novamente." }]);
                setOcupado(false);
                setUploadingFoto(false);
                return;
            } finally {
                setUploadingFoto(false);
            }
        }

        const textoFinal = msg ? (fotoUrl ? `${msg}\n[Foto comprovante]: ${fotoUrl}` : msg) : `[Foto comprovante]: ${fotoUrl}`;
        const novaMsgUsuario = {
            autor: "eu",
            texto: msg || "Enviei uma foto como comprovante:",
            foto: fotoUrl,
            timestamp: Date.now(),
        };

        setTexto("");
        handleRemoveFoto();
        setMensagens((m) => [...m, novaMsgUsuario]);

        try {
            let resposta = "";
            let handoff = false;

            if (rotaUtilizada === "admin") {
                try {
                    const r = await axios.post(`${getBaseUrl()}/api/admin/nina/perguntar`, { texto: textoFinal }).then((res) => res.data);
                    resposta = r.resposta || r.message || r.output || "(sem resposta)";
                    handoff = Boolean(r.handoff);
                } catch (adminErr) {
                    const r = await api("post", "mensagem", { texto: textoFinal });
                    resposta = r.resposta || "(sem resposta)";
                    handoff = Boolean(r.handoff);
                    setRotaUtilizada("nina-web");
                }
            } else {
                const r = await api("post", "mensagem", { texto: textoFinal });
                resposta = r.resposta || "(sem resposta)";
                handoff = Boolean(r.handoff);
            }

            const novaMsgNina = { autor: "nina", texto: resposta, timestamp: Date.now() };
            const conversa = [...mensagens, novaMsgUsuario, novaMsgNina];
            setMensagens(conversa);
            if (handoff) await transferirParaAtendimento(conversa);
        } catch (err) {
            setMensagens((m) => [...m, { autor: "erro", texto: motivo(err), timestamp: Date.now() }]);
        } finally {
            setOcupado(false);
        }
    }

    if (estado.etapa !== "pronto") {
        return (
            <div className={styles.messagesArea}>
                {estado.etapa === "carregando" ? (
                    <p className={styles.loading}>Procurando a sua loja…</p>
                ) : (
                    <div className={styles.ninaAviso}>
                        <p role="alert">{estado.erro}</p>
                        {estado.code === "email_nao_confirmado" && (
                            <div style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%", maxWidth: "320px", marginTop: "6px" }}>
                                <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                                    <button
                                        type="button"
                                        className={`ap-btn ap-btn-sm ${abaValidacao === "whatsapp" ? "ap-btn-primary" : "ap-btn-quiet"}`}
                                        onClick={() => { setAbaValidacao("whatsapp"); setErroWa(""); }}
                                    >
                                        Validar via WhatsApp
                                    </button>
                                    <button
                                        type="button"
                                        className={`ap-btn ap-btn-sm ${abaValidacao === "email" ? "ap-btn-primary" : "ap-btn-quiet"}`}
                                        onClick={() => { setAbaValidacao("email"); setErroWa(""); }}
                                    >
                                        Link por Email
                                    </button>
                                </div>

                                {abaValidacao === "whatsapp" ? (
                                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", background: "rgba(255,255,255,0.03)", padding: "12px", borderRadius: "8px", border: "1px solid var(--rule, #333)" }}>
                                        <label style={{ fontSize: "12px", textAlign: "left", color: "var(--ink-soft)" }}>
                                            Número do WhatsApp com DDD:
                                        </label>
                                        <div style={{ display: "flex", gap: "6px" }}>
                                            <input
                                                type="tel"
                                                value={telefoneWa}
                                                onChange={(e) => setTelefoneWa(e.target.value)}
                                                placeholder="(44) 99999-9999"
                                                className="ap-field-input"
                                                style={{ flex: 1, padding: "6px 10px", fontSize: "13px" }}
                                                disabled={enviandoWa}
                                            />
                                            <button
                                                type="button"
                                                className="ap-btn ap-btn-primary ap-btn-sm"
                                                onClick={enviarCodigoWa}
                                                disabled={enviandoWa || !telefoneWa.trim() || cooldownWa > 0}
                                            >
                                                {enviandoWa ? "Enviando…" : cooldownWa > 0 ? `Reenviar (${cooldownWa}s)` : waEnviado ? "Reenviar Código" : "Enviar Código"}
                                            </button>
                                        </div>

                                        {waEnviado && (
                                            <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "4px" }}>
                                                <label style={{ fontSize: "12px", textAlign: "left", color: "var(--ink-soft)" }}>
                                                    Código de 6 dígitos recebido:
                                                </label>
                                                <div style={{ display: "flex", gap: "6px" }}>
                                                    <input
                                                        type="text"
                                                        value={codigoWa}
                                                        onChange={(e) => setCodigoWa(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                                        placeholder="123456"
                                                        maxLength={6}
                                                        className="ap-field-input"
                                                        style={{ flex: 1, padding: "6px 10px", fontSize: "15px", letterSpacing: "2px", textAlign: "center" }}
                                                        disabled={verificandoWa}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="ap-btn ap-btn-primary ap-btn-sm"
                                                        onClick={validarCodigoWa}
                                                        disabled={verificandoWa || codigoWa.length !== 6}
                                                    >
                                                        {verificandoWa ? "Validando…" : "Confirmar"}
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {msgWa && <p style={{ fontSize: "12px", color: "var(--success, #10b981)", margin: "2px 0 0" }}>{msgWa}</p>}
                                        {erroWa && <p style={{ fontSize: "12px", color: "var(--danger, #ef4444)", margin: "2px 0 0" }}>{erroWa}</p>}
                                    </div>
                                ) : (
                                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", padding: "8px" }}>
                                        {linkEnviado ? (
                                            <>
                                                <p style={{ fontSize: "13px" }}>Mandamos um link para <strong>{auth.currentUser?.email}</strong>. Clique nele e confirme.</p>
                                                <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                                                    <button type="button" className="ap-btn ap-btn-primary ap-btn-sm" onClick={jaConfirmei}>Já confirmei</button>
                                                    <button
                                                        type="button"
                                                        className="ap-btn ap-btn-quiet ap-btn-sm"
                                                        onClick={mandarLink}
                                                        disabled={cooldownEmail > 0}
                                                    >
                                                        {cooldownEmail > 0 ? `Reenviar (${cooldownEmail}s)` : "Reenviar link por email"}
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <p style={{ fontSize: "13px" }}>Enviaremos um link de confirmação para o email da sua conta (<strong>{auth.currentUser?.email}</strong>).</p>
                                                <button
                                                    type="button"
                                                    className="ap-btn ap-btn-primary ap-btn-sm"
                                                    onClick={mandarLink}
                                                    disabled={cooldownEmail > 0}
                                                >
                                                    {cooldownEmail > 0 ? `Reenviar (${cooldownEmail}s)` : "Mandar link por email"}
                                                </button>
                                            </>
                                        )}
                                        {msgWa && <p style={{ fontSize: "12px", color: "var(--success, #10b981)", margin: "2px 0 0" }}>{msgWa}</p>}
                                    </div>
                                )}
                            </div>
                        )}
                        {estado.code !== "email_nao_confirmado" && (
                            <button type="button" className="ap-btn" onClick={carregar}>Tentar de novo</button>
                        )}
                        <button
                            type="button"
                            className="ap-btn ap-btn-quiet ap-btn-sm"
                            onClick={() => transferirParaAtendimento()}
                            disabled={transferindo}
                        >
                            {transferindo ? "Transferindo…" : "Falar com atendente"}
                        </button>
                    </div>
                )}
            </div>
        );
    }

    return (
        <>
            <div className={styles.ninaQuem}>
                <span>
                    <strong>Atendimento: {estado.loja?.nome || "Sua Loja"}</strong>
                    <small>Assistente virtual oficial Auuii</small>
                </span>
                <button
                    type="button"
                    className={styles.ninaQuemBtn}
                    onClick={() => transferirParaAtendimento()}
                    disabled={transferindo || ocupado}
                >
                    {transferindo ? "Transferindo…" : "Falar com atendente"}
                </button>
            </div>
            <div className={styles.messagesArea} aria-live="polite">
                {mensagens.length === 0 && <p className={styles.placeholder}>Olá! Sou a {PERSONA}. Como posso ajudar?</p>}
                {mensagens.map((m, i) => (
                    <div key={i} className={`${styles.message} ${m.autor === "eu" ? styles.sent : styles.received}`}>
                        <div className={styles.senderName}>{m.autor === "eu" ? "Você" : m.autor === "erro" ? "Erro" : PERSONA}</div>
                        {m.texto && <p>{m.texto}</p>}
                        {m.foto && (
                            <a href={m.foto} target="_blank" rel="noopener noreferrer">
                                <img src={m.foto} alt="Comprovante" className={styles.messagePhoto} />
                            </a>
                        )}
                    </div>
                ))}
                {(ocupado || uploadingFoto) && (
                    <div className={styles.typingIndicator}>
                        {uploadingFoto ? "Enviando comprovante..." : `A ${PERSONA} está digitando…`}
                    </div>
                )}
                {erroTransferencia && <p className={styles.transferError} role="alert">{erroTransferencia}</p>}
                <div ref={fimRef} />
            </div>

            {fotoPreview && (
                <div className={styles.photoPreviewBar}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <img src={fotoPreview} alt="Preview" className={styles.photoPreviewThumb} />
                        <span>Foto selecionada</span>
                    </div>
                    <button type="button" onClick={handleRemoveFoto} className={styles.photoRemoveBtn} title="Remover foto">
                        <FiX size={16} />
                    </button>
                </div>
            )}

            <form onSubmit={mandar} className={styles.inputArea}>
                <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    style={{ display: "none" }}
                    id="support-foto-input"
                />
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className={styles.photoBtn}
                    title="Anexar foto ou comprovante"
                >
                    <FiImage size={18} />
                </button>
                <input
                    value={texto}
                    onChange={(e) => setTexto(e.target.value)}
                    placeholder={fotoFile ? "Adicione uma legenda (opcional)..." : "Digite sua mensagem..."}
                    className={`ap-field-input ${styles.input}`}
                    aria-label="Mensagem pra Nina"
                />
                <button
                    type="submit"
                    className="ap-btn ap-btn-primary"
                    disabled={ocupado || uploadingFoto || (!texto.trim() && !fotoFile)}
                    style={{ borderRadius: "10px", padding: "0 14px", height: "38px" }}
                >
                    <FiSend size={16} aria-hidden="true" />
                </button>
            </form>
        </>
    );
};

export default NinaChat;
